import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, output, OutputEmitterRef, Signal } from '@angular/core';
import { MatAnchor, MatButton, MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { ButtonComponent, IconButtonComponent } from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import { OrderHistoryRecordModel } from '@libs/services/models';
import { EnvironmentService } from '@libs/shared/common';
import { CapabilityService } from '@libs/shared/services';
import { CartTrackingService, NavigationTrackingService } from '@libs/shared/tracking';
import { LinkDirective, PriceVisibilityDirective } from '@libs/shared/ui';
import { OrderHistoryNavigationService } from '../order-history-navigation.service';
import { OrderHistoryService } from '../services';

@Component({
  selector: 'ui-order-history-actions',
  templateUrl: './order-history-actions.component.html',
  styleUrls: ['./order-history-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [OrderHistoryService],
  imports: [
    LinkDirective,
    MatTooltip,
    MatIconButton,
    MatButton,
    IconButtonComponent,
    ButtonComponent,
    MatAnchor,
    IconComponent,
    PriceVisibilityDirective,
  ],
})
export class OrderHistoryActionsComponent {
  public order: InputSignal<OrderHistoryRecordModel> = input.required();
  public openSrmForm: OutputEmitterRef<void> = output<void>();
  public openDocuments: OutputEmitterRef<void> = output<void>();
  public deliveryTrackingLink: Signal<string> = computed(() => this.getLinkWithTracking(this.env.links().deliveryTracking));

  public canUseSrm: Signal<boolean> = inject(CapabilityService).isAvailable('services.srm');

  public isAddToCartLoading: Signal<boolean> = computed(() => this.orderHistoryService.isAddingToCart());

  // Reordering pushes the order into the shop cart, which apps running the new app config do not have
  public canReorder: Signal<boolean> = inject(CapabilityService).isAvailable('shop.orderReorder');

  public orderLink: Signal<string> = computed(() => this.orderHistoryNavigation.orderLink(this.order().id));

  private readonly env: EnvironmentService = inject(EnvironmentService);
  private readonly navigationTrackingService: NavigationTrackingService = inject(NavigationTrackingService);
  private readonly orderHistoryService: OrderHistoryService = inject(OrderHistoryService);
  private readonly orderHistoryNavigation: OrderHistoryNavigationService = inject(OrderHistoryNavigationService);
  private readonly cartTracking: CartTrackingService = inject(CartTrackingService);

  public addToCartClicked(): void {
    const order: OrderHistoryRecordModel = this.order();

    void this.orderHistoryService.addOrderToCart(order).then(result => {
      this.cartTracking.trackAddToCartOrder(order, result.errors);
    });
  }

  public onOpenSrmForm(): void {
    this.openSrmForm.emit();
  }

  public onOpenDocuments(): void {
    this.openDocuments.emit();
  }

  private getLinkWithTracking(link: string): string {
    return this.navigationTrackingService.trackingLink(link, 'order-history');
  }
}
